import React, { useState } from 'react';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Shield, Lock, Mail, Crown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPremiumPage = () => {
  const [masterPassword, setMasterPassword] = useState('');
  const [email, setEmail] = useState('');
  const [planId, setPlanId] = useState('yearly');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleActivate = async (e) => {
    e.preventDefault();
    
    if (!masterPassword || !email) {
      toast({
        title: 'Missing Information',
        description: 'Please enter both master password and email.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/payments/admin-activate-premium`,
        null,
        {
          params: {
            email: email,
            plan_id: planId,
            master_password: masterPassword
          }
        }
      );

      toast({
        title: 'Success! 🎉',
        description: response.data.message,
      });

      setEmail('');
    } catch (error) {
      toast({
        title: 'Activation Failed',
        description: error.response?.data?.detail || 'Could not activate premium',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeactivate = async () => {
    if (!masterPassword || !email) {
      toast({
        title: 'Missing Information',
        description: 'Please enter both master password and email.',
        variant: 'destructive',
      });
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/payments/admin-deactivate-premium`,
        null,
        {
          params: {
            email: email,
            master_password: masterPassword
          }
        }
      );

      toast({
        title: 'Success',
        description: response.data.message,
      });

      setEmail('');
    } catch (error) {
      toast({
        title: 'Deactivation Failed',
        description: error.response?.data?.detail || 'Could not deactivate premium',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 to-gray-800 flex items-center justify-center px-4 py-12">
      <Card className="max-w-md w-full p-8 bg-gray-800 border-2 border-amber-500">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-amber-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-gray-900" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Admin Panel</h1>
          <p className="text-gray-400">Premium Access Control</p>
        </div>

        <form onSubmit={handleActivate} className="space-y-6">
          <div>
            <Label htmlFor="masterPassword" className="text-gray-300">
              Master Password
            </Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="masterPassword"
                type="password"
                placeholder="Enter master password"
                value={masterPassword}
                onChange={(e) => setMasterPassword(e.target.value)}
                required
                className="pl-10 bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="email" className="text-gray-300">
              User Email
            </Label>
            <div className="relative mt-1">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <Input
                id="email"
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="pl-10 bg-gray-700 border-gray-600 text-white"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="planId" className="text-gray-300">
              Plan Type
            </Label>
            <select
              id="planId"
              value={planId}
              onChange={(e) => setPlanId(e.target.value)}
              className="w-full mt-1 px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
            >
              <option value="monthly">Monthly (30 days)</option>
              <option value="yearly">Yearly (365 days)</option>
            </select>
          </div>

          <div className="space-y-3">
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold py-6 text-lg"
            >
              <Crown className="w-5 h-5 mr-2" />
              {isLoading ? 'Processing...' : 'Activate Premium'}
            </Button>

            <Button
              type="button"
              onClick={handleDeactivate}
              disabled={isLoading}
              variant="outline"
              className="w-full border-2 border-red-500 text-red-500 hover:bg-red-500 hover:text-white py-6 text-lg"
            >
              Deactivate Premium
            </Button>
          </div>
        </form>

        <div className="mt-8 p-4 bg-gray-700 rounded-lg">
          <p className="text-xs text-gray-400 text-center">
            <Lock className="w-3 h-3 inline mr-1" />
            Admin access only. Keep your master password secure.
          </p>
        </div>

        <div className="mt-4 text-center">
          <p className="text-sm text-gray-500">
            Master Password: <code className="bg-gray-700 px-2 py-1 rounded text-amber-400">LifeWorth2025Admin!</code>
          </p>
        </div>
      </Card>
    </div>
  );
};

export default AdminPremiumPage;
